import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useJsApiLoader,
  GoogleMap,
  Marker,
  DirectionsRenderer,
} from '@react-google-maps/api';
import { MdDirections, MdMyLocation, MdClose, MdDirectionsCar, MdDirectionsWalk, MdDirectionsBus } from 'react-icons/md';
import { GOOGLE_MAPS_API_KEY, DEFAULT_MAP_CENTER } from '../../config/googleMaps';
import './PropertyMap.css';

const TRAVEL_MODES = [
  { mode: 'DRIVING', label: 'Drive', icon: MdDirectionsCar },
  { mode: 'WALKING', label: 'Walk', icon: MdDirectionsWalk },
  { mode: 'TRANSIT', label: 'Transit', icon: MdDirectionsBus },
];

const MAP_OPTIONS = {
  fullscreenControl: true,
  streetViewControl: true,
  mapTypeControl: false,
  zoomControl: true,
};

const formatDuration = (seconds) => {
  if (!seconds || seconds <= 0) return '';
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem ? `${hrs} hr ${rem} min` : `${hrs} hr`;
};

const formatDistance = (meters) => {
  if (!meters || meters <= 0) return '';
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(1)} km`;
};

/**
 * In-app Google Maps view for a property, with turn-by-turn directions from
 * the user's current location (or a chosen origin) to the property.
 *
 * @param {Object} destination - { lat, lng } for the property
 * @param {string} [label] - Property name / address shown in info window
 * @param {string} [address] - Address string used as fallback origin text
 * @param {number} [height=320] - Map height in px
 * @param {boolean} [isAuthenticated=false] - Whether the user is logged in (directions require auth)
 * @param {Function} [onRequireAuth] - Called when an unauthenticated user attempts directions
 */
const PropertyMap = ({ destination, label, address, height = 320, isAuthenticated = false, onRequireAuth }) => {
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  const destCoords = useMemo(() => {
    const lat = Number(destination?.lat);
    const lng = Number(destination?.lng);
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      return { lat, lng };
    }
    return DEFAULT_MAP_CENTER;
  }, [destination?.lat, destination?.lng]);

  const [travelMode, setTravelMode] = useState('DRIVING');
  const [directions, setDirections] = useState(null);
  const [routeSummary, setRouteSummary] = useState(null);
  const [directionsError, setDirectionsError] = useState('');
  const [origin, setOrigin] = useState(null);
  const [loadingRoute, setLoadingRoute] = useState(false);
  const [showDirectionsPanel, setShowDirectionsPanel] = useState(false);
  const directionsServiceRef = useRef(null);
  const mapRef = useRef(null);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  const resetRoute = useCallback(() => {
    setDirections(null);
    setRouteSummary(null);
    setDirectionsError('');
    setOrigin(null);
    setShowDirectionsPanel(false);
  }, []);

  const requestDirections = useCallback(
    (originCoords, mode = travelMode) => {
      if (!originCoords || !destCoords) return;
      if (!directionsServiceRef.current && window.google?.maps) {
        directionsServiceRef.current = new window.google.maps.DirectionsService();
      }
      const service = directionsServiceRef.current || (window.google?.maps ? new window.google.maps.DirectionsService() : null);
      if (!service) return;

      setLoadingRoute(true);
      setDirectionsError('');
      service.route(
        {
          origin: originCoords,
          destination: destCoords,
          travelMode: window.google.maps.TravelMode[mode],
          provideRouteAlternatives: false,
        },
        (result, status) => {
          setLoadingRoute(false);
          if (status === 'OK' && result) {
            setDirections(result);
            const leg = result?.routes?.[0]?.legs?.[0];
            if (leg) {
              setRouteSummary({
                distance: leg.distance?.text || formatDistance(leg.distance?.value),
                duration: leg.duration?.text || formatDuration(leg.duration?.value),
                startAddress: leg.start_address,
                endAddress: leg.end_address,
              });
            }
          } else {
            setDirections(null);
            setRouteSummary(null);
            const statusMessages = {
              'ZERO_RESULTS': 'No route found between your location and the property.',
              'NOT_FOUND': 'Could not locate the destination on the map.',
              'OVER_QUERY_LIMIT': 'Map directions quota exceeded. Please try again later.',
              'REQUEST_DENIED': 'Directions request was denied.',
              'INVALID_REQUEST': 'Invalid directions request.',
            };
            setDirectionsError(statusMessages[status] || `Directions failed (${status}).`);
          }
        }
      );
    },
    [destCoords, travelMode]
  );

  const handleGetDirections = useCallback(() => {
    if (!isAuthenticated) {
      if (typeof onRequireAuth === 'function') onRequireAuth();
      return;
    }
    if (!navigator.geolocation) {
      setDirectionsError('Geolocation is not supported by your browser. Use "From location" or open in Google Maps.');
      return;
    }
    setLoadingRoute(true);
    setDirectionsError('');
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const userCoords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        setOrigin(userCoords);
        setShowDirectionsPanel(true);
        requestDirections(userCoords, travelMode);
      },
      () => {
        setLoadingRoute(false);
        setDirectionsError('Unable to access your location. Please enable location permissions or open in Google Maps.');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  }, [isAuthenticated, onRequireAuth, requestDirections, travelMode]);

  const handleTravelModeChange = useCallback(
    (mode) => {
      setTravelMode(mode);
      if (origin) {
        requestDirections(origin, mode);
      }
    },
    [origin, requestDirections]
  );

  const openInGoogleMaps = useCallback(() => {
    const dest = `${destCoords.lat},${destCoords.lng}`;
    const url = origin
      ? `https://www.google.com/maps/dir/?api=1&origin=${origin.lat},${origin.lng}&destination=${dest}&travelmode=${travelMode.toLowerCase()}`
      : `https://www.google.com/maps/search/?api=1&query=${dest}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, [destCoords, origin, travelMode]);

  // Reset route when destination changes.
  useEffect(() => {
    resetRoute();
  }, [destCoords, resetRoute]);

  if (loadError) {
    return (
      <div className="property-map property-map--error">
        <MdDirections />
        <p>Unable to load the map. {loadError?.message || ''}</p>
        <button type="button" className="property-map__fallback-btn" onClick={openInGoogleMaps}>
          Open in Google Maps
        </button>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="property-map property-map--loading" style={{ height }}>
        <div className="property-map__skeleton" />
      </div>
    );
  }

  return (
    <div className="property-map">
      <div className="property-map__header">
        <div className="property-map__title">
          <MdDirections />
          <span>Location & Directions</span>
        </div>
        <div className="property-map__actions">
          {!directions && (
            <button
              type="button"
              className="property-map__btn property-map__btn--primary"
              onClick={handleGetDirections}
              disabled={loadingRoute}
            >
              <MdMyLocation />
              {loadingRoute
                ? 'Locating…'
                : isAuthenticated
                  ? 'Get directions'
                  : 'Login for directions'}
            </button>
          )}
          <button
            type="button"
            className="property-map__btn property-map__btn--ghost"
            onClick={openInGoogleMaps}
          >
            Open in Google Maps
          </button>
        </div>
      </div>

      <div className="property-map__travel-modes">
        {TRAVEL_MODES.map(({ mode, label, icon: Icon }) => (
          <button
            key={mode}
            type="button"
            className={`property-map__travel-mode ${travelMode === mode ? 'active' : ''}`}
            onClick={() => handleTravelModeChange(mode)}
            disabled={!origin && !directions}
          >
            <Icon />
            <span>{label}</span>
          </button>
        ))}
      </div>

      <div className="property-map__viewport" style={{ height }}>
        <GoogleMap
          mapContainerStyle={{ width: '100%', height: '100%' }}
          center={directions ? undefined : destCoords}
          zoom={14}
          onLoad={onMapLoad}
          options={MAP_OPTIONS}
        >
          {!directions && <Marker position={destCoords} title={label || address || 'Property'} />}
          {directions && <DirectionsRenderer directions={directions} />}
        </GoogleMap>
      </div>

      {routeSummary && (
        <div className="property-map__route-summary">
          <div className="property-map__route-stat">
            <span className="property-map__route-stat-label">Distance</span>
            <span className="property-map__route-stat-value">{routeSummary.distance}</span>
          </div>
          <div className="property-map__route-stat">
            <span className="property-map__route-stat-label">Duration</span>
            <span className="property-map__route-stat-value">{routeSummary.duration}</span>
          </div>
          {routeSummary.startAddress && (
            <div className="property-map__route-endpoints">
              <span className="property-map__route-from">From: {routeSummary.startAddress}</span>
              <span className="property-map__route-to">To: {routeSummary.endAddress || label || address}</span>
            </div>
          )}
        </div>
      )}

      {directionsError && (
        <div className="property-map__error" role="alert">{directionsError}</div>
      )}

      {showDirectionsPanel && directions && (
        <button
          type="button"
          className="property-map__btn property-map__btn--clear"
          onClick={resetRoute}
        >
          <MdClose /> Clear route
        </button>
      )}
    </div>
  );
};

export default PropertyMap;
